import type { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { Format } from './routes/Dashboard/components/DesignsGallery/DesignsGallery.types';
import { withWindow } from '@shared/utils/withWindow';

const category = 'messaging.card';
const PUBLIC_SHOP_CATEGORY = 'marketing.messaging.card';
const RECIPIENT_ECARD_CATEGORY = 'guest.messaging.ecard';

interface CardDraftCreatedArgs {
  page?: 'pdpModal';
  draftId: string;
  themeId: string;
  stationeryTemplateCategory: StationeryTemplateCategoryEnum;
  existingDraftsCount: number;
  subtotalInMinorUnits?: number;
  isFavorite: boolean;
  isDigital?: boolean;
  isPremiumDesign: boolean;
  format: 'paper' | 'premiumDigital';
}

export interface ExitCardPdpModalArgs {
  name: 'breadcrumbsRootLink' | 'breadcrumbsCategoryLink' | 'closeButton';
}

const _isInPublicShop = () => {
  return withWindow(global => {
    return !global.location.pathname.match(/^\/(.*)\/edit\/cards(\/.*)?$/gi);
  }, false);
};

const _isInRecipientEcardView = () => {
  return withWindow(global => {
    return !!global.location.pathname.match(/^\/card\/(\w{10})$/i);
  }, false);
};

const getPagePrefix = () => {
  return _isInPublicShop() ? 'marketing' : 'admin';
};

const getActionCategory = () => {
  return _isInRecipientEcardView() ? RECIPIENT_ECARD_CATEGORY : _isInPublicShop() ? PUBLIC_SHOP_CATEGORY : category;
};

const cardTelemetry = createTelemetryObject({
  pages: {
    homeRoute: () => ({
      category,
      page: 'home',
      pagePrefix: getPagePrefix(),
    }),
    draftsRoute: () => ({
      category,
      page: 'drafts',
      pagePrefix: getPagePrefix(),
    }),
    ordersRoute: () => ({
      category,
      page: 'orders',
      pagePrefix: getPagePrefix(),
    }),
    orderDetailsRoute: () => ({
      category,
      page: 'orderDetails',
      pagePrefix: getPagePrefix(),
    }),
    existingDraftsWarningDialog: () => ({
      category,
      page: 'existingDraftsWarningDialog',
      pagePrefix: getPagePrefix(),
    }),
    restrictedInternationalShippingWarningDialog: () => ({
      category,
      page: 'restrictedInternationalShippingWarningDialog',
      pagePrefix: getPagePrefix(),
    }),
    productPdpModal: () => ({
      category,
      page: 'pdpModal',
      pagePrefix: getPagePrefix(),
    }),
    designGalleryFiltersDrawer: () => ({
      category,
      page: 'designGalleryFiltersDrawer',
      pagePrefix: getPagePrefix(),
    }),
    orderConfirmationConciergeRoute: () => ({
      category,
      page: 'orderConfirmationConcierge',
      pagePrefix: getPagePrefix(),
    }),
    premiumEcardRecipientView: () => ({
      category,
      page: 'premiumEcardRecipientView',
      pagePrefix: 'guest',
    }),
    orderTracking: () => ({
      category,
      page: 'orderTracking',
      pagePrefix: getPagePrefix(),
    }),
    cartPreviewRoute: () => ({
      category,
      page: 'cartPreview',
      pagePrefix: getPagePrefix(),
    }),
    cartAddedRoute: () => ({
      category,
      page: 'cartAdded',
      pagePrefix: getPagePrefix(),
    }),
    cartPostCheckoutRoute: () => ({
      category,
      page: 'cartPostCheckout',
      pagePrefix: getPagePrefix(),
    }),
  },
  actions: {
    buttonInteracted: (extraInfo: Record<string, unknown>) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo,
    }),
    cardDraftCreated: ({ page, ...extraInfo }: CardDraftCreatedArgs) => ({
      category: getActionCategory(),
      action: 'CardDraftCreated',
      page,
      extraInfo: extraInfo,
    }),
    primaryNavInteracted: (destination: 'home' | 'drafts' | 'orders' | 'favorites' | 'imagine') => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: 'primaryNav_goToRoute',
        destination,
      },
    }),
    valuePropLinkInteracted: (propTelemetryId: string, slideIndex: number = 0) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: 'gallery_valueProp',
        value: propTelemetryId,
        slideIndex,
      },
    }),
    //=================================
    // PDP Modal
    //=================================
    pdpCustomizeNowClicked: (args: { themeId: string; supportedFormats: Format; format: 'paper' | 'digital' | 'premiumDigital' }) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: 'pdpModal_customizeNowClicked',
        themeId: args.themeId,
        supportedFormats: args.supportedFormats,
        selectedFormat: args.format,
      },
    }),
    customizerProductChoiceGroupClicked: (args: { from: 'paper' | 'premiumDigital'; to: 'paper' | 'premiumDigital'; themeId: string }) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: 'customizer_productChoiceGroupClicked',
        from: args.from,
        to: args.to,
        themeId: args.themeId,
      },
    }),
    redirectToCategoryShopAs404: (args: { page: 'pdp' | 'shop'; themeId: string }) => ({
      category: getActionCategory(),
      action: 'InvalidCardShopRouteRedirected',
      extraInfo: {
        route: args.page,
        themeId: args.themeId,
      },
    }),
    cardDraftDeleted: (args: { themeId: string; format: StationeryDraftFormat; isPremiumDesign: boolean; stationeryTemplateCategory: StationeryTemplateCategoryEnum }) => ({
      category: getActionCategory(),
      action: 'CardDraftDeleted',
      extraInfo: {
        themeId: args.themeId,
        format: args.format,
        isPremiumDesign: args.isPremiumDesign,
        stationeryTemplateCategory: args.stationeryTemplateCategory,
      },
    }),

    premiumDigitalRecipientViewError: (args: { orderNumber: string }) => ({
      category: getActionCategory(),
      action: 'PremiumDigitalRecipientView',
      actionType: 'error',
      extraInfo: {
        orderNumber: args.orderNumber,
      },
    }),

    markTemplateAsFavorite: (args: { source: 'shop' | 'favoritesGallery' | 'pdp'; themeId: string; templateCategory: StationeryTemplateCategoryEnum; isFavorite: boolean }) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: `markTemplateAsFavorite`,
        source: args.source,
        themeId: args.themeId,
        templateCategory: args.templateCategory,
        isFavorite: args.isFavorite,
      },
    }),

    /**
     * Intended for use in order history + order details routes
     */
    premiumDigitalOrderCtaClick: (args: {
      source: 'orderDetails' | 'orderHistory' | 'orderTracking';
      name: 'goToOrderDetails' | 'trackDelivery' | 'sendMoreOrResend' | 'selectRecipientsAndSend' | 'orderMatchingPaperCard' | 'contactSupport' | 'editOrder';
      orderNumber: string;
      themeId: string;
      stationeryTemplateCategory: StationeryTemplateCategoryEnum;
    }) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: `${args.source}_premiumDigital_${args.name}Cta`,
        themeId: args.themeId,
        orderNumber: args.orderNumber,
        stationeryTemplateCateogry: args.stationeryTemplateCategory,
      },
    }),
    recipientPremiumEcardClick: (source: 'interactiveCard' | 'footer_logo' | 'footer_help' | 'footer_about' | 'footer_browseDesigns') => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: `recipientPremiumEcard_${source}Cta`,
      },
    }),

    premiumOrderTrackingStatusFilterClick: (args: {
      status: string;
      totalRowCount: number;
      filteredRowCount: number;
      stationeryTemplateCategory: StationeryTemplateCategoryEnum;
    }) => ({
      category: getActionCategory(),
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: `orderTracking_premiumDigital_statusFilterCta`,
        status: args.status,
        stationeryTemplateCateogry: args.stationeryTemplateCategory,
        filteredRowCount: args.filteredRowCount,
        totalRowCount: args.totalRowCount,
      },
    }),

    unknownStatusEncountered: (args: { status: string; context: 'orderTracking' | 'messageDetails'; eventId?: string; orderNumber?: string; messageBatchId?: string }) => ({
      category: getActionCategory(),
      action: 'UnknownStatusEncountered',
      actionType: 'error',
      extraInfo: {
        status: args.status,
        context: args.context,
        eventId: args.eventId,
        orderNumber: args.orderNumber,
        messageBatchId: args.messageBatchId,
      },
    }),
  },
});

export const {
  TelemetryProvider,
  useTelemetry: useCardTelemetry,
  enrichTelemetryExtraInfo: enrichCardTelemetryExtraInfo,
} = createTelemetry(cardTelemetry, undefined, undefined, {
  stationeryTemplateCategory: null,
} as {
  stationeryTemplateCategory: StationeryTemplateCategoryEnum | null;
});
